import { useEffect, useMemo, useRef, useState } from "react";
import type { AppStore, GoogleServices, Services, SyncConnection } from "@otrack/app-shell";
import { initialSyncStatus, SyncEngine, type LocalStore, type SyncStatus } from "@otrack/local-store";
import { createApi, type TokenProvider } from "./api";

export interface Account {
  label: string | null;
  getToken: TokenProvider;
  signOut?: () => void;
  /**
   * How this app shows Google's consent for calendar access, and where Google sends the browser
   * back; absent where Google Calendar can't be connected.
   */
  googleConsent?: { returnTo: "web" | "desktop"; open: (url: string) => void };
}

/**
 * Runs sync for the signed-in account and builds the Services the shell needs. `origin` is where
 * ontrack's API lives: the page's own origin on the web, the configured server on desktop.
 */
export function useSyncServices(store: AppStore, local: LocalStore, account: Account, origin: string = window.location.origin): Services {
  const [status, setStatus] = useState<SyncStatus>(initialSyncStatus);
  const [engine, setEngine] = useState<SyncEngine | null>(null);
  const getToken = account.getToken;
  const api = useMemo(() => createApi(origin, getToken), [origin, getToken]);

  // Google Calendar keeps one sync per account, so its services stay the same object while
  // sync status changes; Connect always runs the account's latest flow.
  const consentRef = useRef(account.googleConsent);
  consentRef.current = account.googleConsent;
  const canConnect = account.googleConsent !== undefined;
  const google = useMemo<GoogleServices | null>(() => (canConnect
    ? {
        api: api.google,
        connect: async () => {
          const consent = consentRef.current;
          if (consent) consent.open(await api.googleConsent(consent.returnTo));
        },
        storage: { get: (key) => local.getValue(key), set: (key, value) => local.setValue(key, value) }
      }
    : null), [api, local, canConnect]);

  useEffect(() => {
    let cancelled = false;
    let stopListening = () => {};
    let current: SyncEngine | null = null;
    void local.getDeviceId().then((deviceId) => {
      if (cancelled) return;
      current = new SyncEngine(local, api, deviceId, setStatus, (operations) => store.getState().receiveRemote(operations));
      stopListening = store.getState().onLocalChange(() => current?.schedule());
      current.start();
      setEngine(current);
    });
    return () => {
      cancelled = true;
      stopListening();
      current?.stop();
      setEngine(null);
      setStatus(initialSyncStatus);
    };
  }, [api, local, store]);

  return useMemo<Services>(() => {
    const sync: SyncConnection = { mode: "signed-in", status, account: account.label, ...(account.signOut ? { signOut: account.signOut } : {}), ...(engine ? { syncNow: () => void engine.sync() } : {}) };
    return { sync, assistant: (request) => api.assistant(request), google };
  }, [account, api, engine, google, status]);
}
